import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shuffle, RotateCcw, X, Sparkles } from 'lucide-react';
import { streamChat } from '../../api/chat.js';
import { ensureBootstrap, getActiveCharacter, buildSystemPrompt } from '../../lib/characterCards.js';
import { ensurePresetsBootstrap, getActivePreset } from '../../lib/presets.js';
import { getFlag } from '../../lib/flags.js';
import { saelState } from '../../components/Crab.jsx';
import './Tarot.css';

/* ============================================================
   星夜塔罗 · línkè 模块版
   原型 1:1 移植自 D:\克\LingKe\astral-tarot（星夜塔罗）.jsx
   仅微调两处样式：
   - .app 的 min-height: 100vh 去掉（外层 app-shell 已撑住高度）
   - 底部 padding 缩小（外层 app-shell 已给 tabbar 留位）
   ============================================================ */

// ============ 牌面数据 ============
// 大阿卡纳 22张: [num, name, en, numeral, symbol, upright, reversed, message]
const rawMajor = [
  [0, '愚者', 'The Fool', '0', '⟡', '新开始 · 纯真 · 跃入未知 · 自由', '鲁莽 · 犹豫 · 逃避责任', '相信过程，带着赤子之心出发'],
  [1, '魔术师', 'The Magician', 'I', '∞', '创造 · 显化 · 才华 · 意志力', '操纵 · 自我怀疑 · 浪费才华', '你拥有所需的一切，现在去用它'],
  [2, '女祭司', 'The High Priestess', 'II', '☾', '直觉 · 潜意识 · 秘密 · 内在智慧', '忽视直觉 · 隐瞒 · 停留表面', '向内听，真正的答案在深处'],
  [3, '皇后', 'The Empress', 'III', '♀', '丰盛 · 滋养 · 创造力 · 母性', '依赖 · 过度 · 停滞 · 忽略自己', '让生命自然开花，温柔地拥抱此刻'],
  [4, '皇帝', 'The Emperor', 'IV', '♛', '权威 · 秩序 · 稳定 · 结构', '专制 · 僵硬 · 失控 · 抗拒规则', '建立你的边界，做自己的主导者'],
  [5, '教皇', 'The Hierophant', 'V', '✟', '传统 · 信念 · 指引 · 仪式', '反叛 · 质疑体制 · 非主流', '在传统中寻根，或勇敢走自己的路'],
  [6, '恋人', 'The Lovers', 'VI', '♡', '爱 · 结合 · 选择 · 和谐', '失衡 · 分离 · 错误选择 · 不忠', '跟随心的方向，它知道真相'],
  [7, '战车', 'The Chariot', 'VII', '⚔', '胜利 · 意志 · 前进 · 掌控', '失控 · 方向混乱 · 自我怀疑', '握紧缰绳，朝你决定的方向全力前进'],
  [8, '力量', 'Strength', 'VIII', '∞', '内在力量 · 勇气 · 温柔 · 耐心', '自我怀疑 · 软弱 · 压抑情绪', '温柔比蛮力更强，驯服心中的狮子'],
  [9, '隐士', 'The Hermit', 'IX', '✧', '独处 · 内省 · 智慧 · 指引', '孤立 · 拒绝帮助 · 迷失', '向内走，答案在安静中显现'],
  [10, '命运之轮', 'Wheel of Fortune', 'X', '☸', '命运 · 转变 · 循环 · 机遇', '坏运气 · 停滞 · 抗拒改变', '轮子在转，顺着潮流而非逆流'],
  [11, '正义', 'Justice', 'XI', '⚖', '公正 · 真相 · 因果 · 平衡', '不公 · 逃避责任 · 失衡', '诚实面对，你种什么就收什么'],
  [12, '倒吊人', 'The Hanged Man', 'XII', '☥', '暂停 · 换视角 · 放下 · 等待', '拖延 · 抗拒 · 无意义的牺牲', '停一下，倒过来看，世界不一样'],
  [13, '死神', 'Death', 'XIII', '☠', '结束 · 转化 · 新生 · 放下', '抗拒改变 · 停滞 · 害怕失去', '该结束的让它结束，新的会来'],
  [14, '节制', 'Temperance', 'XIV', '⚗', '平衡 · 融合 · 耐心 · 中道', '失衡 · 过度 · 冲突 · 急躁', '慢慢来，在两端之间找到那条路'],
  [15, '恶魔', 'The Devil', 'XV', '⛧', '束缚 · 欲望 · 执念 · 阴影', '觉醒 · 解脱 · 打破枷锁', '锁是松的，只是你没发现'],
  [16, '塔', 'The Tower', 'XVI', '⚡', '突变 · 崩塌 · 觉醒 · 真相', '避免灾难 · 渐变 · 仍在挣扎', '旧的坍塌，是为了让真的立起来'],
  [17, '星星', 'The Star', 'XVII', '✦', '希望 · 疗愈 · 灵感 · 信念', '失望 · 失去信念 · 自我怀疑', '黑暗中抬头，星星一直都在'],
  [18, '月亮', 'The Moon', 'XVIII', '☽', '幻觉 · 潜意识 · 不确定 · 直觉', '困惑解除 · 真相浮现 · 焦虑消退', '看起来的不一定是真的，但感受是真的'],
  [19, '太阳', 'The Sun', 'XIX', '☀', '喜悦 · 成功 · 活力 · 纯粹', '暂时阴霾 · 延迟的快乐 · 傲慢', '像孩子一样笑，光就在你身上'],
  [20, '审判', 'Judgement', 'XX', '♆', '觉醒 · 召唤 · 救赎 · 新阶段', '自我评判 · 拒绝召唤 · 怀疑', '听见那个声音了吗？是时候站起来'],
  [21, '世界', 'The World', 'XXI', '◉', '完成 · 圆满 · 达成 · 整合', '未完成 · 延迟 · 缺少整合', '你绕了一圈，回到更完整的自己'],
];

// 小阿卡纳 56张: [suit, rank, name, en, upright, reversed, message]
const rawMinor = [
  // 权杖 Wands 火
  ['wands', 1, '权杖一', 'Ace of Wands', '灵感 · 创造 · 新机会 · 激情', '延迟 · 缺动力 · 错失机会', '一束火种，从这里点燃'],
  ['wands', 2, '权杖二', 'Two of Wands', '规划 · 远见 · 抉择 · 个人力量', '恐惧未知 · 缺乏计划 · 停滞', '站在高处，该选哪条路'],
  ['wands', 3, '权杖三', 'Three of Wands', '扩展 · 远方机会 · 进取', '障碍 · 延迟 · 近视', '船已出港，耐心等回响'],
  ['wands', 4, '权杖四', 'Four of Wands', '庆祝 · 稳定 · 归属 · 和谐', '不稳 · 内部冲突 · 缺乏和谐', '找到一个可以落地的地方'],
  ['wands', 5, '权杖五', 'Five of Wands', '竞争 · 冲突 · 挑战 · 混乱', '和解 · 避免冲突 · 内部分歧', '争论有时是成长的开始'],
  ['wands', 6, '权杖六', 'Six of Wands', '胜利 · 认可 · 成就 · 荣耀', '失败 · 自负 · 延迟胜利', '被看见了，允许自己享受'],
  ['wands', 7, '权杖七', 'Seven of Wands', '坚守立场 · 防御 · 挑战', '放弃 · 被淹没 · 不堪重负', '你站的位置值得守护'],
  ['wands', 8, '权杖八', 'Eight of Wands', '迅速 · 消息 · 行动 · 进展', '延误 · 阻碍 · 失控', '事情动起来了，跟上节奏'],
  ['wands', 9, '权杖九', 'Nine of Wands', '坚持 · 韧性 · 最后一步', '偏执 · 耗尽 · 放弃', '离终点只剩一步，再撑一下'],
  ['wands', 10, '权杖十', 'Ten of Wands', '重担 · 责任 · 疲惫', '放下 · 分担 · 卸下负担', '能不能先放下一些？'],
  ['wands', 11, '权杖侍从', 'Page of Wands', '新点子 · 热情 · 学习者 · 信使', '迟疑 · 坏消息 · 幼稚', '一个有趣的开端'],
  ['wands', 12, '权杖骑士', 'Knight of Wands', '冲动 · 冒险 · 追求 · 热情', '鲁莽 · 缺方向 · 无耐心', '要去就去，但别把马累垮'],
  ['wands', 13, '权杖皇后', 'Queen of Wands', '热情 · 自信 · 魅力 · 温暖', '嫉妒 · 专横 · 自私', '她知道自己在发光'],
  ['wands', 14, '权杖国王', 'King of Wands', '领导 · 愿景 · 掌控 · 决断', '专制 · 冲动 · 傲慢', '当自己王国的国王'],
  // 圣杯 Cups 水
  ['cups', 1, '圣杯一', 'Ace of Cups', '情感之始 · 爱 · 灵感 · 疗愈', '情感阻塞 · 失望 · 空心', '心的容器被满满斟上'],
  ['cups', 2, '圣杯二', 'Two of Cups', '结合 · 伙伴 · 爱的连接', '失衡 · 分裂 · 争吵', '两颗心遇见彼此'],
  ['cups', 3, '圣杯三', 'Three of Cups', '庆祝 · 友谊 · 欢聚 · 共鸣', '过度 · 小团体 · 孤立', '有这些人真好'],
  ['cups', 4, '圣杯四', 'Four of Cups', '冷漠 · 重估 · 无聊 · 错过', '醒来 · 新机会 · 觉察', '有什么被你忽略了吗？'],
  ['cups', 5, '圣杯五', 'Five of Cups', '失去 · 悲伤 · 遗憾', '接受 · 前行 · 释怀', '还有两个杯子立着'],
  ['cups', 6, '圣杯六', 'Six of Cups', '怀旧 · 童真 · 回忆 · 温情', '困于过去 · 无法放下', '有些温柔记得就好'],
  ['cups', 7, '圣杯七', 'Seven of Cups', '选择 · 幻想 · 迷失 · 混乱', '清晰 · 决定 · 现实', '别被幻影晃了眼'],
  ['cups', 8, '圣杯八', 'Eight of Cups', '离开 · 寻找意义 · 放弃', '停留 · 害怕改变', '知道什么不是，也是一种答案'],
  ['cups', 9, '圣杯九', 'Nine of Cups', '满足 · 愿望成真 · 喜悦', '虚荣 · 不满 · 浅层快乐', '你值得这份被满足'],
  ['cups', 10, '圣杯十', 'Ten of Cups', '圆满 · 家的幸福 · 和谐', '家庭冲突 · 价值分歧', '这就是你想要的那种幸福'],
  ['cups', 11, '圣杯侍从', 'Page of Cups', '情感信号 · 创意 · 好奇', '情绪化 · 不成熟 · 失望', '心里有话想说'],
  ['cups', 12, '圣杯骑士', 'Knight of Cups', '浪漫 · 理想 · 追随心', '多情 · 不切实际 · 情绪化', '带着梦骑行'],
  ['cups', 13, '圣杯皇后', 'Queen of Cups', '直觉 · 同理 · 情感智慧', '过度依赖 · 被情绪淹没', '她读懂所有未说出口的'],
  ['cups', 14, '圣杯国王', 'King of Cups', '情感稳定 · 同情 · 智慧', '情绪化 · 操纵 · 冷漠', '在情绪里保持稳'],
  // 宝剑 Swords 风
  ['swords', 1, '宝剑一', 'Ace of Swords', '清晰 · 真相 · 突破 · 新想法', '混乱 · 错误信息 · 模糊', '一剑斩开迷雾'],
  ['swords', 2, '宝剑二', 'Two of Swords', '僵局 · 两难 · 回避 · 平衡', '决定 · 释放 · 选边', '闭眼不是答案'],
  ['swords', 3, '宝剑三', 'Three of Swords', '心碎 · 悲伤 · 痛苦 · 背叛', '愈合 · 释放 · 宽恕', '哭出来，不是软弱'],
  ['swords', 4, '宝剑四', 'Four of Swords', '休息 · 恢复 · 冥想 · 暂停', '躁动 · 耗尽 · 无法休息', '该让自己停一停了'],
  ['swords', 5, '宝剑五', 'Five of Swords', '冲突 · 失败 · 惨胜', '和解 · 妥协 · 退让', '赢了但失去了什么'],
  ['swords', 6, '宝剑六', 'Six of Swords', '过渡 · 离开 · 走出困境', '滞留 · 未完成 · 返回', '船已经开了'],
  ['swords', 7, '宝剑七', 'Seven of Swords', '欺骗 · 策略 · 暗中行事', '坦白 · 归还 · 被揭穿', '有人在悄悄做什么'],
  ['swords', 8, '宝剑八', 'Eight of Swords', '困局 · 自我限制 · 恐惧', '解脱 · 新视角 · 释放', '绳子其实是松的'],
  ['swords', 9, '宝剑九', 'Nine of Swords', '焦虑 · 恐惧 · 失眠 · 折磨', '希望 · 直面 · 恐惧消退', '半夜的噩梦不是真相'],
  ['swords', 10, '宝剑十', 'Ten of Swords', '终结 · 背叛 · 低谷 · 崩溃', '重生 · 恢复 · 谷底回升', '触底了就该反弹了'],
  ['swords', 11, '宝剑侍从', 'Page of Swords', '好奇 · 信息 · 警觉 · 直率', '八卦 · 谎言 · 冲动', '眼睛转得很快'],
  ['swords', 12, '宝剑骑士', 'Knight of Swords', '冲刺 · 决心 · 直接', '鲁莽 · 攻击 · 无耐心', '冲得太快会撞墙'],
  ['swords', 13, '宝剑皇后', 'Queen of Swords', '清醒 · 独立 · 直言 · 智慧', '冷酷 · 挑剔 · 苛刻', '她不会骗自己'],
  ['swords', 14, '宝剑国王', 'King of Swords', '理性 · 公正 · 权威 · 逻辑', '专横 · 冷漠 · 滥用权威', '头脑清晰，心别太硬'],
  // 钱币 Pentacles 土
  ['pentacles', 1, '钱币一', 'Ace of Pentacles', '机会 · 物质起点 · 丰盛之种', '错失 · 延迟 · 不稳', '手中有一颗金色种子'],
  ['pentacles', 2, '钱币二', 'Two of Pentacles', '平衡 · 适应 · 多任务', '失衡 · 超负荷 · 混乱', '在两件事之间跳舞'],
  ['pentacles', 3, '钱币三', 'Three of Pentacles', '合作 · 技能 · 团队 · 认可', '孤立 · 低质量 · 不合作', '三个人一起把事做好'],
  ['pentacles', 4, '钱币四', 'Four of Pentacles', '保守 · 积累 · 控制', '慷慨 · 释放 · 不稳', '握得太紧会压死自己'],
  ['pentacles', 5, '钱币五', 'Five of Pentacles', '匮乏 · 孤立 · 困顿', '恢复 · 帮助 · 度过难关', '再冷也别忽略那盏灯'],
  ['pentacles', 6, '钱币六', 'Six of Pentacles', '慷慨 · 分享 · 慈善 · 平衡', '自私 · 债务 · 不平等', '有能力给，就给'],
  ['pentacles', 7, '钱币七', 'Seven of Pentacles', '耐心 · 评估 · 长期投资', '缺乏回报 · 短视 · 失耐心', '果子还在长，再等等'],
  ['pentacles', 8, '钱币八', 'Eight of Pentacles', '勤奋 · 专注 · 精进技艺', '完美主义 · 低质量 · 分心', '一锤一锤地打，功夫不白费'],
  ['pentacles', 9, '钱币九', 'Nine of Pentacles', '自给自足 · 享受成果', '过度工作 · 物质孤独', '你靠自己走到这里，值得'],
  ['pentacles', 10, '钱币十', 'Ten of Pentacles', '家族财富 · 传承 · 稳固', '家族冲突 · 失去根基', '一代人的积累在你身上'],
  ['pentacles', 11, '钱币侍从', 'Page of Pentacles', '学习 · 机会 · 学生心态', '拖延 · 懒惰 · 无目标', '开始学就对了'],
  ['pentacles', 12, '钱币骑士', 'Knight of Pentacles', '勤奋 · 可靠 · 踏实 · 持久', '固执 · 停滞 · 无聊', '慢，但不会错'],
  ['pentacles', 13, '钱币皇后', 'Queen of Pentacles', '务实 · 温暖 · 滋养 · 稳', '物质主义 · 忽视自我 · 焦虑', '她的温柔可以落地'],
  ['pentacles', 14, '钱币国王', 'King of Pentacles', '富裕 · 成功 · 稳定权威', '贪婪 · 物质至上 · 管理失败', '让财富成为根，不是锁'],
];

const SUIT_META = {
  major: { label: '大阿卡纳', sym: '✧', color: '#c9a961', accent: 'rgba(201, 169, 97, 0.4)' },
  wands: { label: '权杖 · 火', sym: '🜂', color: '#d97757', accent: 'rgba(217, 119, 87, 0.35)' },
  cups: { label: '圣杯 · 水', sym: '🜄', color: '#6ca8d4', accent: 'rgba(108, 168, 212, 0.35)' },
  swords: { label: '宝剑 · 风', sym: '🜁', color: '#b8b5c7', accent: 'rgba(184, 181, 199, 0.35)' },
  pentacles: { label: '钱币 · 土', sym: '🜃', color: '#9bb583', accent: 'rgba(155, 181, 131, 0.35)' },
};

const RANK_NUMERAL = { 1: 'Ace', 2: 'II', 3: 'III', 4: 'IV', 5: 'V', 6: 'VI', 7: 'VII', 8: 'VIII', 9: 'IX', 10: 'X', 11: 'Page', 12: 'Knight', 13: 'Queen', 14: 'King' };

const MAJOR = rawMajor.map(([num, name, en, numeral, sym, up, rev, msg]) => ({
  id: `major-${num}`, name, en, numeral, sym, up, rev, msg, suit: 'major'
}));

const MINOR = rawMinor.map(([suit, rank, name, en, up, rev, msg]) => ({
  id: `${suit}-${rank}`, name, en, numeral: RANK_NUMERAL[rank], sym: SUIT_META[suit].sym, up, rev, msg, suit, rank
}));

const CARDS = [...MAJOR, ...MINOR];

const SPREADS = {
  single: {
    name: '日常指引',
    sub: 'Single Card',
    desc: '抽一张，给此刻的自己',
    count: 1,
    positions: ['此刻'],
  },
  three: {
    name: '时间之流',
    sub: 'Past · Present · Future',
    desc: '看事情的来龙去脉',
    count: 3,
    positions: ['过去', '现在', '未来'],
  },
  situation: {
    name: '情境解读',
    sub: 'Situation · Action · Outcome',
    desc: '看清情况、找到方向',
    count: 3,
    positions: ['当下情况', '该如何行动', '可能结果'],
  },
};

// ============ 组件 ============

function Stars({ count = 60 }) {
  const stars = useMemo(() => Array.from({ length: count }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: Math.random() * 2 + 0.5,
    delay: Math.random() * 5,
    duration: Math.random() * 3 + 3,
    opacity: Math.random() * 0.5 + 0.3,
  })), [count]);

  return (
    <div className="stars-layer" aria-hidden="true">
      {stars.map(s => (
        <div
          key={s.id}
          className="star"
          style={{
            left: `${s.x}%`,
            top: `${s.y}%`,
            width: `${s.size}px`,
            height: `${s.size}px`,
            opacity: s.opacity,
            animationDelay: `${s.delay}s`,
            animationDuration: `${s.duration}s`,
          }}
        />
      ))}
    </div>
  );
}

function CardBack() {
  return (
    <div className="card-back">
      <div className="cb-border">
        <div className="cb-inner">
          <div className="cb-outer-ring">
            <div className="cb-middle-ring">
              <div className="cb-moon">☾</div>
              <div className="cb-star cb-s1">✦</div>
              <div className="cb-star cb-s2">✧</div>
              <div className="cb-star cb-s3">✦</div>
              <div className="cb-star cb-s4">✧</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CardFace({ card, reversed }) {
  const meta = SUIT_META[card.suit];
  return (
    <div className="card-face" style={{ '--accent': meta.color, '--glow': meta.accent }}>
      <div className="cf-frame">
        <div className={`cf-content ${reversed ? 'is-reversed' : ''}`}>
          <div className="cf-top">
            <span className="cf-numeral">{card.numeral}</span>
            <span className="cf-suit">{meta.label}</span>
          </div>
          <div className="cf-center">
            <div className="cf-sym">{card.sym}</div>
            <div className="cf-ring-deco"></div>
          </div>
          <div className="cf-bottom">
            <div className="cf-name">{card.name}</div>
            <div className="cf-en">{card.en}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Card({ card, reversed, flipped, onClick, index, position, stage }) {
  return (
    <div className="card-slot" style={{ '--idx': index }}>
      {position && <div className="card-position">{position}</div>}
      <div
        className={`card-wrap ${flipped ? 'is-flipped' : ''} ${stage === 'drawing' && !flipped ? 'is-tappable' : ''}`}
        onClick={!flipped && stage === 'drawing' ? onClick : undefined}
      >
        <div className="card-3d">
          <div className="card-side card-back-side">
            <CardBack />
          </div>
          <div className="card-side card-face-side">
            <CardFace card={card} reversed={reversed} />
          </div>
        </div>
      </div>
      {flipped && <div className="card-tag">{reversed ? '逆位' : '正位'}</div>}
    </div>
  );
}

function DetailModal({ data, onClose }) {
  if (!data) return null;
  const { card, reversed, position } = data;
  const meta = SUIT_META[card.suit];
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ '--accent': meta.color, '--glow': meta.accent }} onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} aria-label="关闭">
          <X size={18} />
        </button>
        <div className="modal-header">
          <div className="modal-pos">{position}</div>
          <div className="modal-numeral">{card.numeral}</div>
          <div className="modal-name">{card.name}</div>
          <div className="modal-en">{card.en}</div>
          <div className="modal-suit">{meta.label} · {reversed ? '逆位' : '正位'}</div>
        </div>
        <div className="modal-sym" style={{ transform: reversed ? 'rotate(180deg)' : 'none' }}>
          {card.sym}
        </div>
        <div className="modal-body">
          <div className="modal-section">
            <div className="modal-label">关键词</div>
            <div className="modal-text">{reversed ? card.rev : card.up}</div>
          </div>
          <div className="modal-section">
            <div className="modal-label">给你的话</div>
            <div className="modal-msg">"{card.msg}"</div>
            {reversed && <div className="modal-hint">逆位：含义翻转或被阻碍。留意能量卡在哪里。</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function TarotApp() {
  const [stage, setStage] = useState('setup'); // setup | shuffling | drawing
  const [spreadKey, setSpreadKey] = useState('three');
  const [question, setQuestion] = useState('');
  const [drawn, setDrawn] = useState([]);
  const [flipped, setFlipped] = useState([]);
  const [detail, setDetail] = useState(null);
  const navigate = useNavigate();

  // Sael 就地解读（2026-07-02，铃：跳走出戏 → 解读直接出现在牌桌上）
  const [reading, setReading] = useState(null); // null | {status:'loading'|'done'|'error', text?, error?}
  const [shownChars, setShownChars] = useState(0); // 打字机：已浮现的字数
  const readingAbortRef = useRef(null);
  const readingRef = useRef(null);

  const spread = SPREADS[spreadKey];

  function buildTarotMessage(afterReading = false) {
    const lines = drawn.map((d, i) => {
      const pos = spread.positions[i];
      return `「${pos}」${d.card.name} ${d.card.en} · ${d.reversed ? '逆位' : '正位'}`;
    });
    return (
      `[塔罗] 我刚抽了「${spread.name}」牌阵` +
      (question.trim() ? `，问的是：「${question.trim()}」` : '，问题只在我心里默念了') +
      `。抽到的是：\n${lines.join('\n')}\n` +
      (afterReading
        ? '你刚才在塔罗桌上已经给我解读过这副牌了，我想再和你多聊聊它。'
        : '帮我解读一下吧。')
    );
  }

  // 塔罗 × Sael 联动：点「让 Sael 解读」→ 牌桌下方浮出解读卡，他就地念牌。
  async function askSael() {
    if (reading?.status === 'loading') return;
    readingAbortRef.current?.abort();
    const ctrl = new AbortController();
    readingAbortRef.current = ctrl;
    setReading({ status: 'loading' });
    saelState('busy'); // 小螃蟹：他在念牌
    setShownChars(0);
    setTimeout(() => readingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 80);
    try {
      ensureBootstrap();
      ensurePresetsBootstrap();
      const character = getActiveCharacter();
      const preset = getActivePreset();
      const system =
        buildSystemPrompt(character, preset) +
        `\n\n# 当前场景\n铃此刻在塔罗页刚抽完牌，点了「让 Sael 解读」，你的话会直接显示在牌桌上。` +
        `请直接给出这副牌的解读：结合她的问题、每张牌的位置与正逆位来讲，温柔而具体，` +
        `像隔着牌桌对她说话，不要用列表和小标题，三五段以内。` +
        `\n（当前时间：${new Date().toLocaleString('zh-CN', { hour12: false })}，铃的本地时间）`;
      let provider = {};
      try {
        provider = {
          api_key: (localStorage.getItem('lingke.chat.provider.apiKey') || '').trim(),
          base_url: (localStorage.getItem('lingke.chat.provider.baseUrl') || '').trim(),
          model: (localStorage.getItem('lingke.chat.provider.model') || '').trim(),
        };
      } catch {}
      const result = await streamChat(
        [{ role: 'user', content: buildTarotMessage() }],
        {
          system,
          temperature: preset?.temperature,
          max_tokens: preset?.max_tokens,
          signal: ctrl.signal,
          provider,
        },
      );
      const text = (result.text || '').trim();
      if (!text) throw new Error('他这次没说出话来');
      setReading({ status: 'done', text });
    } catch (e) {
      if (e?.name === 'AbortError') return;
      setReading({ status: 'error', error: e?.message || '连接失败' });
    } finally {
      saelState('idle');
    }
  }

  // 解读完还想多聊 → 把牌面（和"你已经解读过"的上下文）带进聊天输入框，不自动发送。
  function goChatWithCards() {
    try {
      sessionStorage.setItem('lingke.tarot.handoff', buildTarotMessage(reading?.status === 'done'));
    } catch {}
    navigate('/chat');
  }

  // 打字机：解读到齐后逐字浮现；开了「减弱动效」就整段直出。
  useEffect(() => {
    if (reading?.status !== 'done') return;
    const total = reading.text.length;
    if (getFlag('reduce_motion')) {
      setShownChars(total);
      return;
    }
    const t = setInterval(() => {
      setShownChars((n) => {
        if (n + 2 >= total) {
          clearInterval(t);
          return total;
        }
        return n + 2;
      });
    }, 24);
    return () => clearInterval(t);
  }, [reading]);

  // 离开塔罗页时掐掉未完成的解读请求
  useEffect(() => () => readingAbortRef.current?.abort(), []);

  const startShuffle = () => {
    setDetail(null);
    setFlipped([]);
    readingAbortRef.current?.abort();
    setReading(null);
    setShownChars(0);
    setStage('shuffling');
    setTimeout(() => {
      const arr = [...CARDS];
      for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
      }
      const shuffled = arr;
      const picked = shuffled.slice(0, spread.count).map(c => ({
        card: c,
        reversed: Math.random() < 0.3,
      }));
      setDrawn(picked);
      setStage('drawing');
    }, 2200);
  };

  const flipCard = (i) => {
    if (flipped.includes(i)) return;
    setFlipped(prev => [...prev, i]);
  };

  const reset = () => {
    setStage('setup');
    setDrawn([]);
    setFlipped([]);
    setDetail(null);
    readingAbortRef.current?.abort();
    setReading(null);
    setShownChars(0);
  };

  const allFlipped = drawn.length > 0 && flipped.length === drawn.length;

  return (
    <div className="tarot-app">

      <Stars count={70} />

      <div className="container">
        <header className="header">
          <div className="header-deco">
            <span className="line"></span>
            <span>☾ Astral Tarot ☽</span>
            <span className="line"></span>
          </div>
          <h1 className="title">星夜塔罗</h1>
          <div className="subtitle">"Listen to what the cards whisper"</div>
        </header>

        {stage === 'setup' && (
          <div className="setup">
            <div className="section-label">✦ 选择牌阵 ✦</div>
            <div className="spreads">
              {Object.entries(SPREADS).map(([key, sp]) => (
                <div
                  key={key}
                  className={`spread-card ${spreadKey === key ? 'is-active' : ''}`}
                  onClick={() => setSpreadKey(key)}
                >
                  <div className="sc-count">{sp.count} CARDS</div>
                  <div className="sc-name">{sp.name}</div>
                  <div className="sc-sub">{sp.sub}</div>
                  <div className="sc-desc">{sp.desc}</div>
                </div>
              ))}
            </div>

            <div className="section-label">✦ 心中默念你的问题 ✦</div>
            <div className="question-box">
              <textarea
                className="question-input"
                placeholder="可以写下来，也可以只在心里想一想……无需具体，但要清晰"
                value={question}
                onChange={e => setQuestion(e.target.value)}
              />
            </div>

            <button className="btn-primary" onClick={startShuffle}>
              <span className="btn-icon">
                <Shuffle size={16} />
                洗 牌 · Begin
              </span>
            </button>
          </div>
        )}

        {stage === 'shuffling' && (
          <div className="shuffling">
            <div className="shuffle-deck">
              <div className="shuffle-card"><CardBack /></div>
              <div className="shuffle-card"><CardBack /></div>
              <div className="shuffle-card"><CardBack /></div>
              <div className="shuffle-card"><CardBack /></div>
            </div>
            <div className="shuffling-text">宇宙正在倾听你的问题……</div>
          </div>
        )}

        {stage === 'drawing' && (
          <div className="drawing">
            <div className="drawing-header">
              {question && <div className="drawing-question">"{question}"</div>}
              <div className="drawing-hint">
                {allFlipped ? '✦ 请凝视它们，听听它们说什么 ✦' : '✦ 轻触每张牌，让它翻开 ✦'}
              </div>
            </div>

            <div className={`cards-layout ${spread.count === 1 ? 'layout-single' : ''}`}>
              {drawn.map((d, i) => (
                <Card
                  key={i}
                  card={d.card}
                  reversed={d.reversed}
                  flipped={flipped.includes(i)}
                  onClick={() => flipCard(i)}
                  index={i}
                  position={spread.positions[i]}
                  stage={stage}
                />
              ))}
            </div>

            {allFlipped && (
              <div className="readings">
                <div className="readings-title">✧ 牌语 · Readings ✧</div>
                <div className="readings-list">
                  {drawn.map((d, i) => {
                    const meta = SUIT_META[d.card.suit];
                    return (
                      <div
                        key={i}
                        className="reading-card"
                        style={{ '--accent': meta.color }}
                        onClick={() => setDetail({ card: d.card, reversed: d.reversed, position: spread.positions[i] })}
                      >
                        <div className="rc-top">
                          <span className="rc-pos">{spread.positions[i]}</span>
                          <span className="rc-orient">{d.reversed ? '逆位 · Reversed' : '正位 · Upright'}</span>
                        </div>
                        <div className="rc-name">{d.card.name} · <span style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic', fontSize: '0.9em', opacity: 0.8 }}>{d.card.en}</span></div>
                        <div className="rc-keywords">{d.reversed ? d.card.rev : d.card.up}</div>
                        <div className="rc-msg">"{d.card.msg}"</div>
                        <div className="rc-more">点击查看完整解读 →</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="actions-row">
              <button className="btn-secondary" onClick={reset}>
                <RotateCcw size={14} /> 重新开始
              </button>
              {allFlipped && (
                <button className="btn-secondary" onClick={startShuffle}>
                  <Shuffle size={14} /> 再抽一次
                </button>
              )}
              {allFlipped && (
                <button
                  className="btn-secondary btn-sael"
                  onClick={askSael}
                  disabled={reading?.status === 'loading'}
                >
                  <Sparkles size={14} />{' '}
                  {reading?.status === 'loading'
                    ? '他正在看牌…'
                    : reading?.status === 'done'
                      ? '再解读一次'
                      : '让 Sael 解读'}
                </button>
              )}
            </div>

            {reading && (
              <div className="sael-reading" ref={readingRef}>
                <div className="sr-title">✦ SAEL 的解读 ✦</div>
                {reading.status === 'loading' && (
                  <div className="sr-waiting">他正凝视着你的牌……</div>
                )}
                {reading.status === 'error' && (
                  <div className="sr-error">
                    <p>没能连上他（{reading.error}）</p>
                    <div className="sr-after">
                      <button className="btn-secondary" onClick={askSael}>
                        再试一次
                      </button>
                      <button className="btn-secondary btn-sael" onClick={goChatWithCards}>
                        去聊天室问他 →
                      </button>
                    </div>
                  </div>
                )}
                {reading.status === 'done' && (
                  <>
                    <div className="sr-text">
                      {reading.text.slice(0, shownChars)}
                      {shownChars < reading.text.length && <span className="sr-caret">▍</span>}
                    </div>
                    {shownChars >= reading.text.length && (
                      <div className="sr-after">
                        <button className="btn-secondary btn-sael" onClick={goChatWithCards}>
                          带着这副牌去找他聊 →
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <DetailModal data={detail} onClose={() => setDetail(null)} />
    </div>
  );
}
